/** Stable id for each top-level cart row (standalone product or whole bundle). */

export type CustomQuestionAnswer = {
  id: number | string;
  value: any;
};

export type BundleCheckoutInfo = {
  firstName?: string;
  lastName?: string;
  teamId?: string;
  jerseyNumber?: string;
  grad_year?: string;
  last_name?: string;
  misc?: string;
  customAnswers?: CustomQuestionAnswer[];
};

export function getStoreCustomInformations(storeDetail: any): any[] {
  const list = storeDetail?.custom_informations;
  if (!Array.isArray(list)) return [];
  return list.filter((item: any) => item && item.deleted_at == null);
}

export function getCustomQuestionLabel(item: any): string {
  return item?.question_detail?.question ?? item?.question ?? "";
}

export function isCustomQuestionRequired(item: any): boolean {
  return Number(item?.is_required) === 1;
}

export function getCustomAnswersFromCartBundles(
  cart: any[]
): CustomQuestionAnswer[] {
  if (!Array.isArray(cart)) return [];
  const answersById = new Map<string, CustomQuestionAnswer>();
  for (const item of cart) {
    if (item?.isSingleProduct) continue;
    const answers = item?.bundle_checkout_info?.customAnswers;
    if (!Array.isArray(answers)) continue;
    answers.forEach((answer) => {
      const key = String(answer?.id);
      if (!key || answersById.has(key)) return;
      answersById.set(key, answer);
    });
  }
  return Array.from(answersById.values());
}

/** Per-bundle custom answers for order-create (includes bundle_group_key). */
export type OrderCustomQuestionAnswer = {
  question_id: number | string;
  answer: string;
  bundle_group_key?: string;
};

/**
 * Build custom_question_answer rows for place-order.
 * - Multi / single bundle: one set of answers per bundle cart line, keyed by cartLineId.
 * - No-bundle callers should pass form answers instead (no bundle_group_key).
 */
export function getPerBundleCustomAnswersForOrder(
  cart: any[]
): OrderCustomQuestionAnswer[] {
  if (!Array.isArray(cart)) return [];
  const rows: OrderCustomQuestionAnswer[] = [];
  for (const item of cart) {
    if (item?.isSingleProduct) continue;
    const answers = item?.bundle_checkout_info?.customAnswers;
    if (!Array.isArray(answers) || answers.length === 0) continue;
    const bundleGroupKey =
      item?.cartLineId != null && item.cartLineId !== ""
        ? String(item.cartLineId)
        : undefined;
    answers.forEach((answer) => {
      if (answer?.id == null) return;
      rows.push({
        question_id: answer.id,
        answer: answer.value != null ? String(answer.value) : "",
        ...(bundleGroupKey ? { bundle_group_key: bundleGroupKey } : {}),
      });
    });
  }
  return rows;
}

export function getBundlePlayerDisplayName(
  info: BundleCheckoutInfo | null | undefined
): string {
  if (!info) return "—";
  const name = [info.firstName, info.lastName]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(" ");
  return name !== "" ? name : "—";
}

export function getBundleTeamDisplayValue(
  storeDetail: any,
  teamId: string | undefined | null
): string {
  if (teamId == null || String(teamId).trim() === "") return "—";
  const teams = storeDetail?.teams;
  if (!Array.isArray(teams)) return String(teamId);
  const team = teams.find((x: any) => String(x.id) === String(teamId));
  return team?.team_name != null ? String(team.team_name) : String(teamId);
}

export function bundleCheckoutInfoToPlayerValues(
  info: BundleCheckoutInfo | null | undefined
) {
  return {
    firstName: info?.firstName ?? "",
    lastName: info?.lastName ?? "",
    teamId: info?.teamId ?? "",
    jerseyNumber: info?.jerseyNumber ?? "",
    grad_year: info?.grad_year ?? "",
    last_name: info?.last_name ?? "",
    misc: info?.misc ?? "",
    customAnswers: Array.isArray(info?.customAnswers)
      ? info.customAnswers
      : [],
  };
}

/** First non-empty checkout field across form step, redux, and bundle player info. */
export function pickCheckoutField(
  ...values: Array<string | number | undefined | null>
): string {
  for (const value of values) {
    if (value == null) continue;
    const normalized = String(value).trim();
    if (normalized !== "") return normalized;
  }
  return "";
}

export function getMaxCartLineId(cart: any[]): number {
  let max = 0;
  for (const row of cart) {
    if (row?.cartLineId != null && !Number.isNaN(Number(row.cartLineId))) {
      max = Math.max(max, Number(row.cartLineId));
    }
  }
  return max;
}

/** Assign cartLineId to any top-level row missing it (migration for older carts). */
export function ensureCartLineIds(cart: any[]): void {
  let max = getMaxCartLineId(cart);
  for (const row of cart) {
    if (row.cartLineId == null || row.cartLineId === "") {
      max += 1;
      row.cartLineId = max;
    }
  }
}

export function getNextCartLineId(cart: any[]): number {
  return getMaxCartLineId(cart) + 1;
}

export function cartHasAnyBundle(cart: any[]): boolean {
  return cart.some((row: any) => row && !row.isSingleProduct);
}

export type BundleRowChoice = {
  cartLineId: number;
  label: string;
};

/** 1-based index of a bundle among bundle rows in cart order. */
export function getBundleDisplayNumber(cart: any[], bundleRow: any): number {
  if (!cart?.length || !bundleRow) return 1;
  const targetLineId = Number(bundleRow.cartLineId);
  let n = 0;
  for (const row of cart) {
    if (!row?.isSingleProduct) {
      n += 1;
      if (
        row === bundleRow ||
        (!Number.isNaN(targetLineId) &&
          Number(row.cartLineId) === targetLineId)
      ) {
        return n;
      }
    }
  }
  return n || 1;
}

export function getBundleDisplayLabel(cart: any[], bundleRow: any): string {
  return `Bundle #${getBundleDisplayNumber(cart, bundleRow)}`;
}

export function getBundleRowChoices(cart: any[]): BundleRowChoice[] {
  const choices: BundleRowChoice[] = [];
  let n = 0;
  for (const row of cart) {
    if (!row?.isSingleProduct) {
      n += 1;
      const player = row?.bundle_checkout_info;
      const playerBit =
        player?.firstName || player?.lastName
          ? ` (${[player.firstName, player.lastName].filter(Boolean).join(" ")})`
          : "";
      choices.push({
        cartLineId: Number(row.cartLineId),
        label: `Bundle #${n}${playerBit}`,
      });
    }
  }
  return choices;
}

export function getMaxCartItemId(cart: any[]): number {
  let max = 0;
  for (const row of cart) {
    if (row?.cartItemId != null && !Number.isNaN(Number(row.cartItemId))) {
      max = Math.max(max, Number(row.cartItemId));
    }
    if (!row?.isSingleProduct && row?.bundle_products?.length) {
      for (const p of row.bundle_products) {
        if (p?.cartItemId != null && !Number.isNaN(Number(p.cartItemId))) {
          max = Math.max(max, Number(p.cartItemId));
        }
      }
    }
  }
  return max;
}

export function findBundleRowByLineId(
  cart: any[],
  cartLineId: number
): any | undefined {
  return cart.find(
    (row: any) =>
      !row.isSingleProduct && Number(row.cartLineId) === Number(cartLineId)
  );
}

/** Stores that charge bundle product prices (type 1) vs add-on singles only (type 3). */
export function shouldCountBundleProductPrices(
  paymentHandleType: number | string | null | undefined
): boolean {
  return Number(paymentHandleType) === 1;
}

/** Attached singles assigned to a bundle are charged on type 1 and type 3 stores. */
export function shouldCountAttachedSinglePrices(
  paymentHandleType: number | string | null | undefined
): boolean {
  const payType = Number(paymentHandleType);
  return payType === 1 || payType === 3;
}

export function getAttachedSinglesForBundle(
  cart: any[],
  bundleCartLineId: number | undefined | null
): any[] {
  if (bundleCartLineId == null) return [];
  const lineId = Number(bundleCartLineId);
  if (Number.isNaN(lineId)) return [];
  return cart.filter(
    (row: any) =>
      row?.isSingleProduct &&
      row.parentBundleLineId != null &&
      row.parentBundleLineId !== "" &&
      Number(row.parentBundleLineId) === lineId
  );
}

export function getBundleProductsSubtotal(bundleRow: any): number {
  let subtotal = 0;
  (bundleRow?.bundle_products || []).forEach((product: any) => {
    subtotal +=
      Number(product?.store_product_detail?.club_price || 0) *
      Number(product?.store_product_detail?.quantity || 0);
  });
  return subtotal;
}

export function getAttachedSinglesSubtotal(
  cart: any[],
  bundleCartLineId: number | undefined | null
): number {
  return getAttachedSinglesForBundle(cart, bundleCartLineId).reduce(
    (sum: number, row: any) =>
      sum + Number(row?.club_price || 0) * Number(row?.quantity || 0),
    0
  );
}

/** Subtotal for one bundle row, respecting store payment_handle_type. */
export function getBundleLineSubtotal(
  cart: any[],
  bundleRow: any,
  paymentHandleType?: number | string | null
): number {
  let subtotal = 0;
  if (shouldCountBundleProductPrices(paymentHandleType)) {
    subtotal += getBundleProductsSubtotal(bundleRow);
  }
  if (shouldCountAttachedSinglePrices(paymentHandleType)) {
    subtotal += getAttachedSinglesSubtotal(cart, bundleRow?.cartLineId);
  }
  return subtotal;
}

/** Line shown above add-on singles, e.g. "Included in Two Jerseys". */
export function getIncludedInBundleText(
  cart: any[],
  parentBundleLineId: number | undefined | null
): string {
  if (parentBundleLineId == null) return "";
  const b = findBundleRowByLineId(cart, parentBundleLineId);
  if (!b) return "";
  return `Included in ${getBundleDisplayLabel(cart, b)}`;
}

export type CartDisplayAttachedSingle = { index: number; item: any };

export type CartDisplayEntry =
  | { kind: "standalone"; index: number; item: any }
  | {
      kind: "bundle";
      index: number;
      item: any;
      attachedSingles: CartDisplayAttachedSingle[];
    };

/** Top-level cart rows for UI: bundle rows include add-on singles nested underneath (same storage array). */
export function buildCartDisplayEntries(cart: any[]): CartDisplayEntry[] {
  if (!cart?.length) return [];

  const attachedByParent = new Map<number, CartDisplayAttachedSingle[]>();
  for (let i = 0; i < cart.length; i++) {
    const row = cart[i];
    if (row?.isSingleProduct && row.parentBundleLineId != null) {
      const pid = Number(row.parentBundleLineId);
      if (Number.isNaN(pid)) continue;
      if (!attachedByParent.has(pid)) attachedByParent.set(pid, []);
      attachedByParent.get(pid)!.push({ index: i, item: row });
    }
  }

  const entries: CartDisplayEntry[] = [];
  for (let i = 0; i < cart.length; i++) {
    const row = cart[i];
    if (row?.isSingleProduct && row.parentBundleLineId != null) {
      continue;
    }
    if (!row?.isSingleProduct) {
      const lineId = Number(row.cartLineId);
      const attached = Number.isNaN(lineId)
        ? []
        : attachedByParent.get(lineId) ?? [];
      entries.push({
        kind: "bundle",
        index: i,
        item: row,
        attachedSingles: attached,
      });
    } else {
      entries.push({ kind: "standalone", index: i, item: row });
    }
  }

  const seen = new Set<number>();
  entries.forEach((e) => {
    if (e.kind === "standalone") seen.add(e.index);
    else {
      seen.add(e.index);
      e.attachedSingles.forEach((a) => seen.add(a.index));
    }
  });
  for (let i = 0; i < cart.length; i++) {
    if (!seen.has(i)) {
      entries.push({ kind: "standalone", index: i, item: cart[i] });
    }
  }

  return entries;
}
